// @flow
import React, { Component, Fragment } from "react";
import { get, bindAll, isEqual, some, noop, attempt } from "lodash";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import mediator from "@tvg/mediator";
import wtx, { type WagerSummary } from "@tvg/api/wtx";
import tpm from "@tvg/api/tpm";
import calculateTotals from "@tvg/api/wtx/BetHelper";
import tvgConf from "@tvg/conf";
import type { TvgConf } from "@tvg/conf/src/types.js.flow";
import performanceUtils from "@tvg/utils/performanceUtils";
import isMobile from "@tvg/utils/mobileUtils";
import BetUtils, { mapSelectionsToNumber } from "@tvg/utils/betSelectionUtils";
import { mapSelectionsToString } from "@tvg/utils/talentPicksUtils";
import {
  resetBetting,
  resetSelection,
  setBetConfirmation,
  setBetAmount,
  setPickLeg,
  setSelectionScratched,
  setPickAction,
  noMandatoryTalentIdSelected,
  setBetTicketState,
  setBetType,
  type Actions as BetActions,
  clearBetRepeatNumber
} from "@tvg/shared-actions/BetActions";
import {
  showMainWagerType,
  type Actions as ProgramActions
} from "@tvg/shared-actions/ProgramActions";
import {
  updateBalance,
  type UpdateBalanceActionType
} from "@tvg/shared-actions/UserActions";
import {
  openLocationSplash,
  setOnLocationGet
} from "@tvg/shared-actions/LocationActions";
import type {
  RaceProgram,
  RaceWagerType,
  BettingInterest
} from "@tvg/types/Race";
import buildColor from "@tvg/atomic-ui/_static/ColorPalette";
import sendToAppsFlyer from "@tvg/utils/appsflyerUtils";

import BetTicket from "@tvg/atomic-ui/_templates/BetTicket";
import TalentPickConfirmationHeader from "@tvg/atomic-ui/_molecule/TalentPickConfirmationHeader";
import { getCurrentProbableValue } from "@tvg/sh-lib-my-bets/utils/potentialReturn";
import type { BetReceipt } from "@tvg/types/Bet";
import { getShowMyBetsCardDesign } from "@tvg/sh-lib-my-bets/redux/selectors";
import uwt from "@tvg/api/uwt";
import { getAccountNumber } from "@tvg/sh-lib-preferences/redux/selectors";
import { onRepeatBetGTM } from "@tvg/sh-lib-my-bets/utils/gtm";

import { getBetSelectionsToShow } from "./utils";
import type { Props } from "./types";
import BetConfirmationModal from "./BetConfirmationModalComponent";

const isScratchedSelections = (selections: string[][]): boolean =>
  some(selections, (selection) => selection.length > 0);

export const getTitleScratchedRunners = (
  scratchedSelections: string[][] = [[]]
): string => {
  if (scratchedSelections.length > 1) {
    return scratchedSelections
      .map((selection, index) => {
        if (selection.length > 0) {
          return `Race ${index + 1} - Horse ${selection.join(
            ", "
          )} just got scratched`;
        }
        return selection;
      })
      .join(" ");
  }

  return `Runner ${scratchedSelections[0].join(", ")} just got scratched`;
};

export class BetTicketComponent extends Component<Props> {
  static defaultProps = {
    betSelections: [[]],
    scratchedSelections: [[]],
    favoriteSelections: [[]],
    isLogged: false,
    showBetConfirmationPreference: "1",
    showBetConfirmation: false,
    betAmount: 0,
    isVisible: false,
    isLocationRequired: false,
    isRequestingLocation: false,
    columnLayout: "single",
    isVideoShown: false,
    isAdmin: false,
    selectTalentIdErrorVisible: false,
    contextBT: "mep",
    selectedTalentId: -1,
    talentPick: null,
    talentPickTriggerOrigin: null,
    pickEditButton: false,
    wtxErrorMessages: "",
    betTicketState: {},
    accountNumber: "",
    betErrorMessageToggle: false,
    fromRepeatBet: false,
    repeatBetType: "",
    showMyBetsCardDesign: false,
    accountId: 0
  };

  constructor(props: Props): void {
    super(props);
    this.tvgConf = tvgConf();
    bindAll(this, [
      "cancelBet",
      "submitBet",
      "setBetAmount",
      "handleCloseWarning",
      "handleBetNow",
      "onRetainRunners",
      "onBackToRace",
      "resetState",
      "placeBetFunction",
      "loginCallback",
      "parseErrorMessage",
      "placePick",
      "submitPick"
    ]);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props.showBetConfirmation !== nextProps.showBetConfirmation) {
      mediator.ios.dispatch({
        type: "TOGGLE_BACKGROUND_COLOR",
        payload: {
          backgroundColor: nextProps.showBetConfirmation
            ? buildColor("blue", "000")
            : buildColor("blue", "500")
        }
      });
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    return (
      !isEqual(nextProps.race, this.props.race) ||
      !isEqual(nextProps.betSelections, this.props.betSelections) ||
      !isEqual(nextProps.bettingInterests, this.props.bettingInterests) ||
      !isEqual(nextProps.showBetConfirmation, this.props.showBetConfirmation) ||
      !isEqual(nextProps.showRepeatBet, this.props.showRepeatBet) ||
      nextProps.betAmount !== this.props.betAmount ||
      !isEqual(nextProps.wagerType, this.props.wagerType) ||
      nextProps.isLogged !== this.props.isLogged ||
      nextProps.isVisible !== this.props.isVisible ||
      nextProps.isVideoShown !== this.props.isVideoShown ||
      nextProps.selectTalentIdErrorVisible !==
        this.props.selectTalentIdErrorVisible ||
      nextProps.selectedTalentId !== this.props.selectedTalentId ||
      !isEqual(nextProps.favoriteSelections, this.props.favoriteSelections) ||
      !isEqual(nextProps.scratchedSelections, this.props.scratchedSelections) ||
      !isEqual(this.props.geolocation, nextProps.geolocation) ||
      this.props.isRequestingLocation !== nextProps.isRequestingLocation ||
      !isEqual(nextProps.talentPick, this.props.talentPick) ||
      !isEqual(nextProps.betTicketState, this.props.betTicketState) ||
      !isEqual(nextProps.betsNumber, this.props.betsNumber)
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (
      get(this.props, "race.raceNumber") !== get(prevProps, "race.raceNumber")
    ) {
      this.resetState();
    }
  }

  componentWillUnmount() {
    this.resetState();
  }

  onRetainRunners() {
    this.props.dispatch(setBetConfirmation(false));
    this.props.dispatch(setBetTicketState({ repeatBetNumber: 1 }));
    this.props.dispatch(setPickAction(null));
    // this timeout is for animations to roll and prevent flicker
    setTimeout(this.resetState, 400);

    mediator.base.dispatch({
      type: "BET_CONFIRMATION:RETAIN_RUNNERS",
      payload: {
        trackName: get(this.props, "race.track.trackName"),
        raceNumber: get(this.props, "race.raceNumber"),
        betType: get(this.props, "wagerType.type.code"),
        runnerType:
          get(this.props, "race.type.code") === "G" ? "Greyhound" : "Horse"
      }
    });
  }

  handleCloseWarning() {
    this.props.dispatch(setSelectionScratched([[]]));
  }

  onBackToRace() {
    this.props.dispatch(setBetConfirmation(false));
    this.props.dispatch(setBetTicketState({ repeatBetNumber: 1 }));
    this.props.dispatch(setPickAction(null));

    // this timeout is for animations to roll and prevent flicker
    setTimeout(() => {
      if (this.props.showMyBetsCardDesign) {
        this.props.dispatch(clearBetRepeatNumber());
      }
      this.props.dispatch(resetSelection());
      this.props.dispatch(setPickLeg(0));
      this.props.dispatch(setSelectionScratched([[]]));
      this.props.dispatch(
        setBetTicketState({
          isBetPlaced: false,
          betConfirmationTitle: "confirm bet",
          randomTalentPhrase: "",
          randomTalentPhraseId: -1
        })
      );
    }, 400);
  }

  onCloseCustomAmountModal = () => {
    mediator.base.dispatch({
      type: "PPM_CLOSE_CUSTOM_AMOUNT"
    });
    this.forceUpdate();
  };

  setBetAmount(value: number, isCustomAmount: boolean) {
    this.props.dispatch(setBetAmount(parseFloat(value)));

    mediator.base.dispatch({
      type: "PPM_SET_BET_AMOUNT",
      payload: {
        betAmount: value,
        isCustomAmount
      }
    });
  }

  resetState() {
    this.props.dispatch(
      setBetTicketState({
        isProcessing: false,
        isBetPlaced: false,
        isBettingError: false,
        errorBetMessage: "",
        betConfirmationTitle: "confirm bet",
        randomTalentPhrase: "",
        randomTalentPhraseId: -1,
        hasFeedback: false,
        feedbackType: "error",
        partialFailure: false,
        partialInfo: "",
        partialInfoDetails: "",
        feedbackTitle: "",
        feedbackMsg: ""
      })
    );
    this.props.dispatch(clearBetRepeatNumber());
  }

  cancelBet(isTrashClicked?: boolean) {
    if (
      this.props.showBetConfirmation &&
      this.props.betTicketState.isBetPlaced
    ) {
      this.props.dispatch(setBetConfirmation(false));
      this.props.dispatch(setBetTicketState({ repeatBetNumber: 1 }));
    } else {
      this.props.dispatch(resetBetting());
      this.resetState();
    }

    if (isTrashClicked) {
      const wt = BetUtils.getDefaultBetType(this.props.race, "", false);
      this.props.dispatch(setBetType(wt));
      this.props.dispatch(showMainWagerType(true));

      mediator.base.dispatch({
        type: "PPM_DELETE_BET_TICKET",
        payload: {
          trackName: get(this.props, "race.track.trackName"),
          raceNumber: get(this.props, "race.raceNumber"),
          betType: get(this.props, "wagerType.group.name"),
          runnerType: get(this.props, "race.isGreyhound")
            ? "Greyhounds"
            : "Horse"
        }
      });
    }
  }

  placeBetFunction() {
    this.resetState();
    if (this.props.showBetConfirmationPreference === "0") {
      this.submitBet();
    } else {
      this.props.dispatch(setBetConfirmation(true));
      this.props.dispatch(
        setBetTicketState({
          betConfirmationTitle: "confirm bet"
        })
      );
    }
  }

  placePick() {
    const isTalentPicksTool =
      get(this.props, "contextBT", "") === "talentPicks";
    const hasValidTalentId = get(this.props, "selectedTalentId", "") !== "";
    if (isTalentPicksTool && !hasValidTalentId && this.props.isAdmin) {
      this.props.dispatch(noMandatoryTalentIdSelected(true));
      return;
    }

    this.props.dispatch(noMandatoryTalentIdSelected(false));
    this.resetState();
    this.props.dispatch(setBetConfirmation(true));
    this.props.dispatch(
      setBetTicketState({
        betConfirmationTitle: "confirm and publish pick"
      })
    );
  }

  loginCallback(error: *, success: *) {
    if (get(success, "status", "fail") === "success") {
      this.props.dispatch(setOnLocationGet(this.placeBetFunction));
    }
  }

  handleBetNow() {
    mediator.base.dispatch({
      type: "PPM_BET_NOW",
      payload: {
        selections: get(this.props, "betSelections").join(", "),
        trackName: get(this.props, "race.track.trackName"),
        raceNumber: get(this.props, "race.raceNumber"),
        betAmount: get(this.props, "betAmount"),
        betType: get(this.props, "wagerType.type.code"),
        runnerType:
          get(this.props, "race.type.code") === "G" ? "Greyhound" : "Horse"
      }
    });

    if (this.props.fromRepeatBet && this.props.repeatBetType) {
      const totals = calculateTotals(
        this.props.betAmount,
        get(this.props, "wagerType"),
        mapSelectionsToNumber(this.props.betSelections)
      );

      onRepeatBetGTM({
        gaEventLabel: get(this.props, "repeatBetType", ""),
        runnerAmount: get(this.props, "betAmount", 0),
        trackName: get(this.props, "race.track.trackName", ""),
        betTotal: totals.betCount * get(this.props, "betAmount", 0),
        betType: get(this.props, "wagerType.group.code", ""),
        isGreyhound: get(this.props, "race.isGreyhound", false),
        runnerSelectionList: get(this.props, "betSelections").join(", "),
        raceNumber: get(this.props, "race.raceNumber", 0)
      });
    }

    if (!this.props.isLogged) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: this.loginCallback,
          triggerAction: "ppm_bet_now"
        }
      });
    } else if (
      !this.props.isLocationRequired ||
      (get(this.props, "geolocation.location.latitude", null) &&
        get(this.props, "geolocation.location.longitude", null))
    ) {
      this.placeBetFunction();
    } else {
      this.props.dispatch(setOnLocationGet(this.placeBetFunction));
      this.props.dispatch(openLocationSplash());
    }
  }

  submitPick() {
    const totals = calculateTotals(
      this.props.betAmount,
      get(this.props, "wagerType"),
      mapSelectionsToNumber(this.props.betSelections)
    );

    const wagerSelection = this.props.betSelections.map((column: string[]) =>
      column.map((selection: string) => ({ order: +selection }))
    );

    const favoriteSelections = this.props.favoriteSelections.map(
      (column: string[]) =>
        column.map((selection: string) => ({ order: +selection }))
    );

    const race = {
      trackAbbr: get(this.props, "race.track.trackCode"),
      raceNumber: get(this.props, "race.raceNumber")
    };

    this.props.dispatch(
      setBetTicketState({
        isProcessing: true
      })
    );

    return tpm
      .placePick(
        wagerSelection,
        favoriteSelections,
        get(this.props, "wagerType.type.code"),
        this.props.betAmount,
        this.props.betAmount * totals.betCount,
        race,
        get(this.props, "talentPick.talent.talentId", "")
      )
      .then(() => {
        this.props.dispatch(
          setBetTicketState({
            isProcessing: false,
            isBetPlaced: true,
            betConfirmationTitle: "Pick placed!"
          })
        );
        this.props.dispatch(setBetConfirmation(true));
      })
      .catch((error) => {
        const errorBetMessage = this.parseErrorMessage(
          get(error, "response.data")
        );

        this.props.dispatch(
          setBetTicketState({
            isProcessing: false,
            isBettingError: true,
            betConfirmationTitle: "Pick not placed!",
            errorBetMessage
          })
        );
      });
  }

  processErrorState(code: number, fulfilled?: number, requested?: number) {
    const message = this.parseErrorMessage(code);
    let partialFailure = false;
    let baseMessage = message;
    if (fulfilled && requested && fulfilled !== requested) {
      partialFailure = true;
      baseMessage = `We were only able to repeat ${fulfilled} of ${requested} bets.`;
    }

    return {
      betConfirmationTitle: "Bet not placed!",
      partialFailure,
      isBettingError: true,
      failed: partialFailure ? (requested || 0) - (fulfilled || 0) : 1,
      feedbackTitle: partialFailure
        ? "Unable to repeat all your bets"
        : "Unable to repeat bets",
      partialInfo: partialFailure ? baseMessage : "",
      partialInfoDetails: partialFailure ? message : "",
      feedbackMsg: !partialFailure ? message : ""
    };
  }

  handleBetSuccess(betCount: number, betCost: number, serialNumber: string) {
    this.props.dispatch(
      setBetTicketState({
        isProcessing: false,
        isBetPlaced: true,
        betConfirmationTitle: "Bet placed!",
        randomTalentPhrase: "Good luck!",
        randomTalentPhraseId: 0
      })
    );
    this.props.dispatch(setBetConfirmation(true));
    performanceUtils.measure("place_bet");

    if (this.props.talentPick && this.props.contextBT === "mep") {
      mediator.base.dispatch({
        type: "TP_PROGRAMPAGE_BET_SUCCESS",
        payload: {
          action: "Bet Success",
          label: get(this.props.talentPick, "wagerType.abbreviation"),
          talentPickOrigin: this.props.talentPickTriggerOrigin,
          tag: mapSelectionsToString(
            get(this.props.talentPick, "selections", [])
          ),
          race: {
            raceNumber: get(this.props.talentPick, "race"),
            track: {
              trackName: get(this.props.talentPick, "trackName")
            },
            type: {
              code: get(this.props.talentPick, "raceType")
            }
          },
          betId: get(this.props, "wagerType.type.id"),
          betAmount: +get(this.props.talentPick, "amount"),
          betType: get(this.props.talentPick, "wagerType.abbreviation")
        }
      });
    } else {
      mediator.base.dispatch({
        type: "BET_CONFIRMATION:BET_SUCCESS",
        payload: {
          selections: mapSelectionsToString(
            get(this.props, "betSelections", [])
          ),
          trackName: get(this.props, "race.track.trackName"),
          raceNumber: get(this.props, "race.raceNumber"),
          betAmount: get(this.props, "betAmount", 1) * betCount,
          runnerAmount: get(this.props, "betAmount"),
          module: "Bet Ticket",
          betType: get(this.props, "wagerType.type.code"),
          betId: get(this.props, "wagerType.type.id"),
          runnerType:
            get(this.props, "race.type.code") === "G" ? "Greyhound" : "Horse",
          wagerSerialNumber: serialNumber
        }
      });
    }

    if (isMobile(this.tvgConf.product)) {
      mediator.ios.dispatch({
        type: "PUSHWOOSH_EVENTS",
        payload: {
          pushwooshTags: {
            LastBet: (+(new Date().getTime() / 1000)).toFixed(0)
          }
        }
      });

      sendToAppsFlyer({
        key: "af_bet_cost",
        values: {
          accountId: this.props.accountNumber.toString(),
          af_revenue: betCost.toString()
        }
      });
    }
  }

  sendGtmRepeatBettingError(errorCode: number, numberOfBets: number = 1) {
    mediator.base.dispatch({
      type: "BET_CONFIRMATION:BET_ERROR",
      payload: {
        error: errorCode,
        module: "Repeat Bet Modal",
        selections: mapSelectionsToString(get(this.props, "betSelections", [])),
        trackName: get(this.props, "race.track.trackName"),
        raceNumber: get(this.props, "race.raceNumber"),
        betAmount: get(this.props, "betAmount"),
        betId: get(this.props, "wagerType.type.id"),
        betType: get(this.props, "wagerType.type.code"),
        runnerType:
          get(this.props, "race.type.code") === "G"
            ? "Greyhounds Racing"
            : "Horse Racing",
        repeatBet: numberOfBets.toString()
      }
    });
  }

  handleRepeatBetMessageError(
    status: Array<BetReceipt>,
    fulfilled: number,
    requested: number
  ) {
    const isRaceClosedError = status.filter(
      (item) =>
        item.error && (item.error.code === 136505 || item.error.code === 136554)
    );
    if (isRaceClosedError.length > 0) {
      let raceClosedErrorCode: number = -1;
      const raceClosedError: BetReceipt = isRaceClosedError[0];
      if (raceClosedError.error) {
        raceClosedErrorCode = raceClosedError.error.code;
      }

      // Race closed error is present amongst the error objects
      return this.processErrorState(raceClosedErrorCode, fulfilled, requested);
    }

    const firstError = status.filter((item) => item.error);
    const error: BetReceipt = firstError[0];
    let errorCode: number = -1;
    if (error.error) {
      errorCode = error.error.code;
    }

    return this.processErrorState(errorCode, fulfilled, requested);
  }

  handleRepeatBetSuccess(
    status: Array<BetReceipt>,
    summary: WagerSummary,
    numberOfBets: number = 1
  ) {
    const { requested, fulfilled } = summary;
    if (requested === fulfilled) {
      this.props.dispatch(
        setBetTicketState({
          isProcessing: false,
          isBetPlaced: true,
          betConfirmationTitle: "Bet placed!",
          repeatBetNumber: fulfilled
        })
      );

      performanceUtils.measure("place_bet");
      mediator.base.dispatch({
        type: "BET_CONFIRMATION:BET_SUCCESS",
        payload: {
          selections: mapSelectionsToString(
            get(this.props, "betSelections", [])
          ),
          trackName: get(this.props, "race.track.trackName"),
          module: "Repeat Bet Modal",
          raceNumber: get(this.props, "race.raceNumber"),
          betAmount: get(this.props, "betAmount"),
          betId: get(this.props, "wagerType.type.id"),
          betType: get(this.props, "wagerType.type.code"),
          runnerType:
            get(this.props, "race.type.code") === "G" ? "Greyhounds" : "Horse",
          repeatBet: numberOfBets.toString()
        }
      });
      if (isMobile(tvgConf().product)) {
        mediator.ios.dispatch({
          type: "PUSHWOOSH_EVENTS",
          payload: {
            pushwooshTags: {
              LastBet: (+(new Date().getTime() / 1000)).toFixed(0)
            }
          }
        });
      }
    } else {
      // RB-29 - gtm guys didnt care about all the errors, sending the 1st
      const failedBets = status.filter((r) => r.statusCode !== 200);
      const failedBetsGtmError = failedBets[0]
        ? get(failedBets[0], "error.code")
        : 400;
      const allFailed = summary.requested === summary.failed;

      this.props.dispatch(
        setBetTicketState({
          isProcessing: false,
          isBettingError: true,
          hasFeedback: true,
          feedbackType: allFailed ? "error" : "warning",
          ...this.handleRepeatBetMessageError(status, fulfilled, requested)
        })
      );

      this.sendGtmRepeatBettingError(failedBetsGtmError, numberOfBets);
    }
  }

  handleBetError(errorCode: number, message: string) {
    let errorBetMessage = this.parseErrorMessage(errorCode);

    if (errorCode === 136457) {
      const replaceObj = {
        _betAmount:
          this.props.talentPick && this.props.contextBT === "mep"
            ? get(this.props.talentPick, "amount", 0).toFixed(2)
            : get(this.props, "betAmount", 0).toFixed(2),
        _minAmount: message.split("=").pop().split(")")[0].trim()
      };
      errorBetMessage = this.props.betErrorMessageToggle
        ? errorBetMessage.replace(
            /_betAmount|_minAmount/gi,
            (matched) => replaceObj[matched]
          )
        : this.parseErrorMessage(errorCode + 1);
      // we increment the errorCode by 1 because the next message is exactly the same as the old message
    }

    this.props.dispatch(
      setBetTicketState({
        isProcessing: false,
        isBettingError: true,
        betConfirmationTitle: "Bet not placed!",
        errorBetMessage
      })
    );

    if (this.props.talentPick && this.props.contextBT === "mep") {
      mediator.base.dispatch({
        type: "TP_PROGRAMPAGE_BET_ERROR",
        payload: {
          action: "Bet Error",
          label: errorBetMessage,
          talentPickOrigin: this.props.talentPickTriggerOrigin,
          tag: mapSelectionsToString(
            get(this.props.talentPick, "selections", [])
          ),
          race: {
            raceNumber: get(this.props.talentPick, "race"),
            track: {
              trackName: get(this.props.talentPick, "trackName")
            },
            type: {
              code: get(this.props.talentPick, "raceType")
            }
          },
          betAmount: get(this.props.talentPick, "amount"),
          betType: get(this.props.talentPick, "wagerType.abbreviation")
        }
      });
    } else {
      mediator.base.dispatch({
        type: "BET_CONFIRMATION:BET_ERROR",
        payload: {
          error: errorCode,
          module: this.props.showBetConfirmation
            ? "Confirm Bet Modal"
            : "Bet Ticket",
          selections: mapSelectionsToString(
            get(this.props, "betSelections", [])
          ),
          runnerAmount: get(this.props, "betAmount"),
          trackName: get(this.props, "race.track.trackName"),
          raceNumber: get(this.props, "race.raceNumber"),
          betAmount: get(this.props, "betAmount"),
          betType: get(this.props, "wagerType.type.code"),
          runnerType:
            get(this.props, "race.type.code") === "G"
              ? "Greyhounds Racing"
              : "Horse Racing"
        }
      });
    }
  }

  handleRepeatBetError(errorCode: number, numberOfBets: number = 1) {
    const errorObj = this.processErrorState(errorCode);

    this.props.dispatch(
      setBetTicketState({
        isProcessing: false,
        isBettingError: true,
        hasFeedback: true,
        feedbackType: "error",
        ...errorObj
      })
    );

    this.sendGtmRepeatBettingError(errorCode, numberOfBets);
  }

  submitBet(numberOfBets?: number) {
    const isRepeatBets = typeof numberOfBets === "number";
    const totals = calculateTotals(
      this.props.betAmount,
      get(this.props, "wagerType"),
      mapSelectionsToNumber(this.props.betSelections)
    );

    const wagerSelection = this.props.betSelections.map((column: string[]) =>
      column.map((selection: string) => ({ order: +selection }))
    );

    const race = {
      trackAbbr: get(this.props, "race.track.trackCode"),
      raceNumber: get(this.props, "race.raceNumber")
    };

    const betCost = this.props.betAmount * totals.betCount;

    performanceUtils.register("place_bet");
    // --------- add overlay processing on betTicket
    this.props.dispatch(
      setBetTicketState({
        isProcessing: true
      })
    );

    return wtx
      .placeBet(
        wagerSelection,
        get(this.props, "wagerType.type.code"),
        this.props.betAmount,
        betCost,
        race,
        get(this.props, "geolocation.location"),
        get(this.props, "geolocation.regions"),
        this.props.accountNumber,
        numberOfBets
      )
      .then((response) => {
        if (response.summary) {
          if (
            response.summary.userBalance !== null &&
            !isNaN(response.summary.userBalance)
          ) {
            // new wtx approach
            this.props.dispatch(updateBalance(response.summary.userBalance));
          } else if (isRepeatBets) {
            uwt.getBalance(this.props.accountId, false).then((res) => {
              const balance = get(res, "data.balance", undefined);
              this.props.dispatch(updateBalance(balance));
            });
          }

          // --------- remove overlay processing on betTicket
          if (isRepeatBets) {
            this.handleRepeatBetSuccess(
              get(response, "status", []),
              response.summary,
              numberOfBets
            );
          } else {
            this.handleBetSuccess(
              totals.betCount,
              betCost,
              get(response, "status[0].success.serialNumber", "")
            );
          }

          if (this.props.showMyBetsCardDesign) {
            this.props.dispatch(
              setBetTicketState({
                repeatBetNumber: numberOfBets
              })
            );

            this.props.dispatch(clearBetRepeatNumber());
          }
        } else if (isRepeatBets) {
          this.props.dispatch(
            setBetTicketState({
              isProcessing: false,
              isBettingError: true,
              hasFeedback: true,
              feedbackType: "warning",
              feedbackTitle: "Unable to repeat all your bets",
              feedbackMsg:
                "We were unable to check if the bets were all successfully submitted. Please check your bet history."
            })
          );
        }
      })
      .catch((error) => {
        if (isRepeatBets) {
          this.handleRepeatBetError(
            get(error.response, "data.code"),
            numberOfBets
          );
        } else {
          this.handleBetError(
            get(error.response, "data.code"),
            get(error.response, "data.message")
          );
        }
      });
  }

  parseErrorMessage(code: number) {
    const defaultMessage = "An error occurred while trying to place your bet.";
    const wtxErrorMessages = attempt(() =>
      JSON.parse(this.props.wtxErrorMessages)
    );
    return wtxErrorMessages[code] || defaultMessage;
  }

  dismissTalentPicksWarning = () => {
    this.props.dispatch(noMandatoryTalentIdSelected(false));
  };

  tvgConf: TvgConf;

  renderTalentHeader = () =>
    this.props.showBetConfirmation &&
    !this.props.pickEditButton &&
    !!get(this.props, "talentPick", null) &&
    get(this.props, "talentPick.talent.pictureUrl", "") !== ""
      ? () => (
          <TalentPickConfirmationHeader
            {...(get(this.props, "talentPick.talent.pictureUrl", "") !== ""
              ? {
                  talentImage: `//${this.tvgConf.config(
                    "domain.desktop"
                  )}/${get(this.props, "talentPick.talent.pictureUrl", "")}`
                }
              : {})}
            message={this.props.betTicketState.randomTalentPhrase || ""}
          />
        )
      : noop;

  render() {
    const wagerType = get(this.props, "wagerType");
    const repetitionsNr =
      typeof this.props.betsNumber === "number"
        ? this.props.betsNumber
        : this.props.betTicketState.repeatBetNumber;

    const probableValue = getCurrentProbableValue(
      get(this.props, "probables"),
      wagerType,
      this.props.betAmount,
      get(this.props, "betSelections", []),
      this.props.showMyBetsCardDesign ? repetitionsNr : 1
    );

    const totals =
      this.props.isVisible && get(this.props, "wagerType.columnCount")
        ? calculateTotals(
            get(this.props, "betAmount"),
            get(this.props, "wagerType"),
            get(this.props, "betSelections")
          )
        : { betCount: 0 };

    return (
      <Fragment>
        <BetTicket
          key="betTicketComponent"
          selections={getBetSelectionsToShow(
            this.props.betSelections,
            get(this.props, "bettingInterests", [])
          )}
          onCancelBet={() => this.cancelBet(true)}
          onPlaceBet={
            this.props.contextBT === "mep" ? this.handleBetNow : this.placePick
          }
          onChangeBetAmount={this.setBetAmount}
          onCloseCustomAmountModal={this.onCloseCustomAmountModal}
          betType={get(this.props, "wagerType.type.code")}
          betTotal={this.props.betAmount * totals.betCount}
          betAmounts={get(this.props, "wagerType.wagerAmounts")}
          minimumWagerAmount={get(wagerType, "minWagerAmount")}
          selectedBetAmount={this.props.betAmount}
          raceTypeCode={get(this.props, "race.type.code")}
          isVisible={this.props.isVisible}
          hasOtherAmount
          isBetDisabled={this.props.betAmount * totals.betCount <= 0}
          isKey={get(this.props, "wagerType.isKey", false)}
          isLeg={get(this.props, "wagerType.legCount", 1) > 1}
          isProcessing={
            this.props.betTicketState.isProcessing ||
            this.props.isRequestingLocation
          }
          raceNumber={this.props.race.raceNumber}
          modalType={this.props.columnLayout === "single" ? "bottom" : "fade"}
          onWarningClose={this.handleCloseWarning}
          scratchedSelectionMessage={getTitleScratchedRunners(
            this.props.scratchedSelections
          )}
          onDismissError={() => this.resetState()}
          isSelectionScratched={isScratchedSelections(
            this.props.scratchedSelections
          )}
          isBettingError={
            !+this.props.showBetConfirmationPreference &&
            this.props.betTicketState.isBettingError
          }
          errorBetTitle={this.props.betTicketState.betConfirmationTitle}
          errorBetMessage={this.props.betTicketState.errorBetMessage}
          contextBT={this.props.contextBT}
          favoriteRunners={this.props.favoriteSelections}
          selectTalentIdErrorVisible={this.props.selectTalentIdErrorVisible}
          dismissTalentPicksWarning={this.dismissTalentPicksWarning}
          isMobile={this.props.columnLayout === "single"}
          inIosAppTablet={
            this.tvgConf.product === "ios2" && this.tvgConf.device === "tablet"
          }
        />
        <BetConfirmationModal
          race={this.props.race}
          wagerType={this.props.wagerType}
          betSelections={this.props.betSelections}
          betAmount={this.props.betAmount}
          showBetConfirmation={this.props.showBetConfirmation}
          isVideoShown={this.props.isVideoShown}
          isProcessing={this.props.betTicketState.isProcessing}
          isBetPlaced={this.props.betTicketState.isBetPlaced}
          isBettingError={this.props.betTicketState.isBettingError}
          betConfirmationTitle={this.props.betTicketState.betConfirmationTitle}
          repeatBetTitle="Repeat bet"
          errorBetMessage={this.props.betTicketState.errorBetMessage}
          bettingInterests={this.props.bettingInterests}
          isOpen={this.props.showBetConfirmation || this.props.showRepeatBet}
          onRetain={this.onRetainRunners}
          onBackToRace={this.onBackToRace}
          onConfirmation={
            this.props.contextBT === "mep" ? this.submitBet : this.submitPick
          }
          resetState={this.resetState}
          modalType={this.props.columnLayout === "single" ? "bottom" : "fade"}
          modalTopChildren={this.renderTalentHeader()}
          contextBT={this.props.contextBT}
          favoriteRunners={this.props.favoriteSelections}
          repetitionsNr={repetitionsNr}
          probableValue={probableValue}
          showRepeatButton={
            !this.props.betTicketState.isBettingError &&
            (this.props.betTicketState.isBetPlaced || !!this.props.betsNumber)
          }
          hasFeedback={this.props.betTicketState.hasFeedback}
          feedbackType={this.props.betTicketState.feedbackType}
          partialFailure={this.props.betTicketState.partialFailure}
          partialInfo={this.props.betTicketState.partialInfo}
          partialInfoDetails={this.props.betTicketState.partialInfoDetails}
          feedbackTitle={this.props.betTicketState.feedbackTitle}
          feedbackMsg={this.props.betTicketState.feedbackMsg}
          showRepeatBet={this.props.showRepeatBet}
        />
      </Fragment>
    );
  }
}

export default connect(
  (
    store,
    props: {
      isVisible: boolean,
      isVideoShown: boolean,
      bettingInterests: BettingInterest[][],
      race: RaceProgram,
      wagerType: RaceWagerType,
      betSelections: string[][],
      columnLayout: "single" | "dual",
      betAmount: number,
      showBetConfirmation: boolean,
      scratchedSelections: string[][],
      showRepeatBet: boolean,
      accountId: number,
      repeatBetsLimit: number
    }
  ) => ({
    bettingInterests: props.bettingInterests,
    race: props.race,
    wagerType: props.wagerType,
    columnLayout: props.columnLayout,
    betSelections: props.betSelections,
    betAmount: props.betAmount,
    showBetConfirmation: props.showBetConfirmation,
    scratchedSelections: props.scratchedSelections,
    isVideoShown: props.isVideoShown,
    isLogged: store.userData.logged,
    accountNumber: get(store, "userData.user.accountNumber", ""),
    isAdmin: get(store, "userData.user.isAdmin"),
    showBetConfirmationPreference: get(
      store,
      "userData.preferences.show_bet_confirm",
      "1"
    ),
    selectTalentIdErrorVisible: get(
      store,
      "TalentProgram.selectTalentIdErrorVisible",
      false
    ),
    geolocation: store.geolocation,
    isLocationRequired: get(store, "locationSplash.isLocationRequired"),
    isRequestingLocation: get(store, "locationSplash.isLoading"),
    wtxErrorMessages: get(store, "capi.messages.wtxErrorMessages"),
    pickEditButton: get(store, "RaceProgram.pickEditButton", true),
    betTicketState: store.BetTicket.config,
    betsNumber: get(store, "BetTicket.repeatBetNumber"),
    showRepeatBet: props.showRepeatBet,
    talentPickTriggerOrigin: get(
      store,
      "RaceProgram.talentPickTriggerOrigin",
      null
    ),
    betErrorMessageToggle: get(
      store,
      "capi.featureToggles.betErrorMessage",
      false
    ),
    showMyBetsCardDesign: getShowMyBetsCardDesign(store),
    accountId: getAccountNumber(store)
  }),
  (
    dispatch: Dispatch<BetActions | ProgramActions | UpdateBalanceActionType>
  ) => ({ dispatch })
)(BetTicketComponent);
